import type { Account, AttendancePayrollPolicy, HrSettings } from '@erp/shared';
import { ChevronDown, Settings2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, Combobox, type ComboboxOption, FormField, Input } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { getAttendanceSettings, saveAttendanceSettings } from '../../lib/attendance.js';
import { getHrSettings, saveHrSettings } from '../../lib/payroll.js';

const SELECT_CLS = 'block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg';

function options(accounts: Account[], type: Account['accountType']): ComboboxOption[] {
  const scoped = accounts.filter((a) => a.isActive && a.accountType === type);
  return (scoped.length ? scoped : accounts.filter((a) => a.isActive))
    .map((a) => ({ value: a.id, label: `${a.code} — ${a.name}`, keywords: `${a.code} ${a.name}` }));
}

/**
 * Payroll's settings, which had no screen at all: without the two GL accounts a
 * run could be drafted and approved and then never posted. Plus the attendance
 * policy — whether absence, lateness and overtime move pay — kept beside the
 * payroll it changes rather than on the attendance page.
 */
export function PayrollSettingsCard({ onSaved }: { onSaved?: () => void }): JSX.Element {
  const { t } = useTranslation('hr');
  const [open, setOpen] = useState(false);
  const [hr, setHr] = useState<HrSettings | null>(null);
  const [policy, setPolicy] = useState<AttendancePayrollPolicy | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void getHrSettings().then(setHr).catch(() => setHr(null));
    void getAttendanceSettings().then((s) => setPolicy(s.payroll)).catch(() => setPolicy(null));
    void api().get<{ items: Account[] }>('/accounts').then((r) => setAccounts(r.data.items)).catch(() => setAccounts([]));
  }, []);

  const expenseAccts = useMemo(() => options(accounts, 'expense'), [accounts]);
  const liabilityAccts = useMemo(() => options(accounts, 'liability'), [accounts]);
  const missingAccounts = hr !== null && (!hr.glAccounts.salariesExpense || !hr.glAccounts.payrollPayable);

  const save = async (): Promise<void> => {
    if (!hr || !policy) return;
    setSaving(true);
    try {
      const [nextHr, nextAtt] = await Promise.all([
        saveHrSettings({ taxEngine: hr.taxEngine, personalAllowance: hr.personalAllowance ?? '0', glAccounts: hr.glAccounts }),
        saveAttendanceSettings({ payroll: policy }),
      ]);
      setHr(nextHr);
      setPolicy(nextAtt.payroll);
      toast.success(t('attendance.sessions.saved'));
      onSaved?.();
    } catch {
      toast.error(t('payroll.settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const check = (key: 'enabled' | 'deductAbsence' | 'deductLate' | 'payOvertime', label: string, hint?: string): JSX.Element => (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4"
        checked={policy?.[key] ?? false}
        disabled={!policy || (key !== 'enabled' && !policy.enabled)}
        onChange={(e) => policy && setPolicy({ ...policy, [key]: e.target.checked })}
      />
      <span>
        <span className="font-medium">{label}</span>
        {hint && <span className="block text-xs text-fg-muted">{hint}</span>}
      </span>
    </label>
  );

  return (
    <Card className="p-0">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-5 py-3 text-start">
        <Settings2 className="h-4 w-4 text-fg-muted" aria-hidden />
        <span className="font-semibold">{t('payroll.settings.title')}</span>
        {missingAccounts && <span className="text-xs text-warning">{t('payroll.settings.accountsMissing')}</span>}
        {policy?.enabled && <span className="text-xs text-success">{t('payroll.settings.attendanceOn')}</span>}
        <ChevronDown className={`ms-auto h-4 w-4 transition ${open ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {open && hr && policy && (
        <div className="flex flex-col gap-5 border-t border-border p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="ps-salaries" label={t('payroll.settings.salariesExpense')}>
              <Combobox
                id="ps-salaries"
                options={expenseAccts}
                value={hr.glAccounts.salariesExpense ?? undefined}
                onChange={(v) => setHr({ ...hr, glAccounts: { ...hr.glAccounts, salariesExpense: v ?? null } })}
                ariaLabel={t('payroll.settings.salariesExpense')}
              />
            </FormField>
            <FormField id="ps-payable" label={t('payroll.settings.payrollPayable')}>
              <Combobox
                id="ps-payable"
                options={liabilityAccts}
                value={hr.glAccounts.payrollPayable ?? undefined}
                onChange={(v) => setHr({ ...hr, glAccounts: { ...hr.glAccounts, payrollPayable: v ?? null } })}
                ariaLabel={t('payroll.settings.payrollPayable')}
              />
            </FormField>
            <FormField id="ps-engine" label={t('payroll.settings.taxEngine')}>
              <select id="ps-engine" className={SELECT_CLS} value={hr.taxEngine} onChange={(e) => setHr({ ...hr, taxEngine: e.target.value as HrSettings['taxEngine'] })}>
                <option value="generic">{t('payroll.settings.engines.generic')}</option>
                <option value="iraq">{t('payroll.settings.engines.iraq')}</option>
              </select>
            </FormField>
            <FormField id="ps-allow" label={t('payroll.settings.personalAllowance')}>
              <Input id="ps-allow" inputMode="decimal" value={hr.personalAllowance ?? ''} onChange={(e) => setHr({ ...hr, personalAllowance: e.target.value })} />
            </FormField>
          </div>

          <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
            <h3 className="text-sm font-semibold">{t('payroll.settings.attendanceTitle')}</h3>
            {check('enabled', t('payroll.settings.attendanceEnabled'), t('payroll.settings.attendanceEnabledHint'))}
            <div className="grid grid-cols-1 gap-3 ps-6 md:grid-cols-2">
              {check('deductAbsence', t('payroll.settings.deductAbsence'), t('payroll.settings.deductAbsenceHint'))}
              {check('deductLate', t('payroll.settings.deductLate'), t('payroll.settings.deductLateHint'))}
              {check('payOvertime', t('payroll.settings.payOvertime'))}
              <div className="grid grid-cols-2 gap-3">
                <FormField id="ps-mult" label={t('payroll.settings.multiplier')}>
                  <Input
                    id="ps-mult"
                    inputMode="decimal"
                    value={policy.overtimeMultiplier}
                    disabled={!policy.enabled}
                    onChange={(e) => setPolicy({ ...policy, overtimeMultiplier: e.target.value })}
                  />
                </FormField>
                <FormField id="ps-div" label={t('payroll.settings.dayRate')}>
                  <select
                    id="ps-div"
                    className={SELECT_CLS}
                    value={policy.dayRateDivisor}
                    disabled={!policy.enabled}
                    onChange={(e) => setPolicy({ ...policy, dayRateDivisor: e.target.value as AttendancePayrollPolicy['dayRateDivisor'] })}
                  >
                    <option value="thirty">{t('payroll.settings.divisors.thirty')}</option>
                    <option value="scheduled">{t('payroll.settings.divisors.scheduled')}</option>
                  </select>
                </FormField>
              </div>
            </div>
          </div>

          <div>
            <Button onClick={() => void save()} loading={saving}>{t('attendance.sessions.save')}</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
